import { Select } from "antd";
import { Field, Formik, Form } from "formik";
import React, { useEffect, useState } from "react";
import DatePicker from "react-multi-date-picker";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import ModalPopUp from "@/Components/common/ModalPopUp";
import { RejectIcon } from "@/Components/Icons/RejectIcon";

import {
  handleFetchCandidateDetails,
  setLoading,
  toggleEditBankCandidateComplianceModal,
  updateBankCandidateCompliance,
} from "../../../reducers/clientcandidatedetail";
import { apiDecrypt } from "../../../utilities/keyEncryption";
import { ComplianceStatusList, showErrorToast } from "../../../utilities/utils";
import ButtonLoader from "../../ButtonLoader";
import { RequiredSymbol } from "../../common/RequiredSymbol";
import HelpReportFeebackUploadIcon from "../../Icons/HelpReportFeebackUploadIcon";
import { SelectDownIcon } from "../../Icons/SelectDownIcon";
import { SelectRemoveIcon } from "../../Icons/SelectRemoveIcon";

const EditBankCandidateComplianceModal = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { editBankCandidateComplianceModal } = useSelector(
    (state) => state.clientCandidateDetail,
  );
  const { candidateDetails, selectedDocuments, candidateDocuments } =
    useSelector((state) => state.clientCandidateDetail);

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);

  const handleReloadTable = () => {
    dispatch(handleFetchCandidateDetails(apiDecrypt(id)));
    dispatch(setLoading(true));
    // dispatch(
    //     fetchCandidateDocuments({
    //         candidate_id: candidateDetails.candidate_id,
    //     }),
    // );
  };

  // get values from redux - what client selected in compliance table
  const getSelectedDocuments = () => {
    const selectedDocs = [];
    for (const category in candidateDocuments) {
      candidateDocuments[category].forEach((doc) => {
        if (selectedDocuments.includes(doc.document_id)) {
          const shortFilename = doc.filename.split("/").pop();
          selectedDocs.push({
            document_id: doc.document_id,
            name: doc.name,
            statusName: doc.status_title,
            statusId: doc.status,
            description: doc.description,
            document_url: doc.document_url,
            filename: shortFilename,
            expiry_date: doc.expiry_date,
            original_filename: doc.original_filename,
            allow_multi_upload: doc.allow_multi_upload,
          });
        }
      });
    }
    return selectedDocs;
  };

  const selectedDocumentDetails = getSelectedDocuments();
  const [fileName, setFileName] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [updatedSelectedDocumentDetails, setUpdatedSelectedDocumentDetails] =
    useState(selectedDocumentDetails);

  // Handle file selection - when user selects a file from the file input element
  const handleFileChange = (e, setFieldValue, values) => {
    let file = e.target.files; // Get the uploaded file

    let existingFiles = Array.isArray(values.compliance_file)
      ? values.compliance_file
      : values.compliance_file
        ? values.compliance_file.split(", ")
        : [];

    if (file) {
      // If the file is not null, add it to the existing files array and update the state.
      // allow multi upload true - allow multiple files to be uploaded in the same compliance
      // allow multi upload false - only one file can be uploaded in the same compliance
      if (selectedDocumentDetails[0]?.allow_multi_upload) {
        // Append new file if multi-upload is allowed
        let existingFilesWithoutNull = existingFiles.filter(
          (f) => f !== "null",
        );
        if (existingFilesWithoutNull.length >= 3) {
          showErrorToast(
            "You cannot upload more than 3 files in this compliance!",
          );
          e.target.value = "";
          return;
        }
        let newFiles = [...file];
        // Check if the file already exists in the array
        let nullIndexes = existingFiles.reduce((tempIndexes, value, index) => {
          if (value === "null") {
            tempIndexes.push(index);
          }
          return tempIndexes;
        }, []); // null indexes from existing files

        // If there are null indexes, replace them with the new file
        if (nullIndexes.length) {
          newFiles.forEach((file, index) => {
            let hasNull = existingFiles.some((ele) => ele === "null");
            if (hasNull) {
              existingFiles[nullIndexes[index]] = file;
            }
          });
        } else {
          newFiles.forEach((file) => {
            if (existingFiles.length < 3) {
              existingFiles.push(file);
            }
          });
        }

        setFieldValue("compliance_file", existingFiles); // Update Formik field value
      } else {
        setFieldValue("compliance_file", [file[0]]); // Update Formik field value
      }
    }
  };

  const getStatusName = (selectedDocument) => {
    if (!selectedDocument?.statusName) return "Awaiting Approval";

    const matchingStatus = ComplianceStatusList.find(
      (status) => status?.name === selectedDocument?.statusName,
    );

    // Return the matched name or handle unknown statuses
    return (
      matchingStatus?.name ||
      (selectedDocument?.statusName === "Not Uploaded"
        ? "Not Uploaded"
        : "Awaiting Approval")
    );
  };

  const handleClose = () => {
    dispatch(toggleEditBankCandidateComplianceModal(false));
    setFileName("");
  };

  useEffect(() => {
    if (!editBankCandidateComplianceModal) {
      setFileName("");
      setSelectedFiles([]);
      setUpdatedSelectedDocumentDetails([]);
    }
  }, [editBankCandidateComplianceModal]);

  const getInitialValues = (selectedDocuments, selectedDocumentDetails) => {
    return {
      compliance_id: selectedDocuments.join(","),
      title:
        selectedDocumentDetails.length > 0
          ? selectedDocumentDetails[0]?.name
          : "",
      status: getStatusName(selectedDocumentDetails[0]) || "",
      expirydate:
        selectedDocumentDetails.length > 0
          ? selectedDocumentDetails[0]?.expiry_date
          : "",
      notApplicable:
        selectedDocumentDetails.length > 0 &&
        selectedDocumentDetails[0]?.statusName === "Not Applicable",
      compliance_file:
        selectedDocumentDetails.length > 0
          ? selectedDocumentDetails[0]?.document_url.map(
              (doc) => doc.original_filename,
            )
          : [],
    };
  };

  const handleSubmit = (
    values,
    { setSubmitting },
    selectedDocumentDetails,
    candidateDetails,
    handleClose,
    handleReloadTable,
    dispatch,
  ) => {
    let allFiles = values.compliance_file.map((ele) =>
      ele === "null" ? null : ele,
    );
    const allAttachmentIds =
      selectedDocumentDetails[0]?.document_url.map(
        (file) => file?.attachment_id,
      ) || "-";

    const postData = {
      candidate_id: candidateDetails.candidate_id,
      compliance_id: values.compliance_id,
      compliance_status: ["Awaiting Approval", "Pending"].includes(
        values.status,
      )
        ? "In-Review"
        : values.status,
      expirydate: values.expirydate,
      not_applicable: values.notApplicable ? 1 : 0,
      compliance_file: allFiles[0] || "",
      extracompliance_file: allFiles[1] || "",
      extracompliance_file1: allFiles[2] || "",
      extra_attachment_id: allAttachmentIds[1] || "",
      extra_attachment_id1: allAttachmentIds[2] || "",
    };

    dispatch(
      updateBankCandidateCompliance({
        postData,
        setSubmitting,
        callback: () => {
          handleClose();
          handleReloadTable();
        },
      }),
    );
  };

  return (
    <ModalPopUp
      show={editBankCandidateComplianceModal}
      panelClass="max-w-[840px] max-sm:max-w-[400px]"
      onClose={handleClose}
    >
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h3 className="text-[20px] font-semibold leading-[normal] text-black">
            Edit Compliance
          </h3>
          <button
            onClick={handleClose}
            className="text-red outline-none ring-0"
          >
            <RejectIcon />
          </button>
        </div>
        <div>
          <Formik
            initialValues={getInitialValues(
              selectedDocuments,
              selectedDocumentDetails,
            )}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            enableReinitialize={true}
            validate={(values) => {
              const errors = {};
              if (
                values.status === "Not Uploaded" &&
                values.compliance_file === "-"
              ) {
                errors.status =
                  "This compliance has not been uploaded by the candidate. If you wish to add this compliance, please update the status and upload a new file.";
              } else if (values.status === "Not Uploaded") {
                errors.status =
                  "This compliance has not been uploaded by the candidate. If you wish to add this compliance, please update the status and upload a new file.";
              } else if (!values.status) {
                errors.status = "Status is required.";
              }
              let tempComplianceFile = values.compliance_file.filter(
                (f) => f !== "null",
              );
              if (
                (Array.isArray(tempComplianceFile) &&
                  tempComplianceFile.length === 0) ||
                (selectedDocumentDetails[0]?.statusName === "Not Uploaded" &&
                  tempComplianceFile[0] === "-")
              ) {
                errors.compliance_file = "Please upload a document.";
              }

              return errors;
            }}
            onSubmit={(values, actions) =>
              handleSubmit(
                values,
                actions,
                selectedDocumentDetails,
                candidateDetails,
                handleClose,
                handleReloadTable,
                dispatch,
              )
            }
          >
            {({
              submitForm,
              isSubmitting,
              errors,
              touched,
              setFieldValue,
              values,
            }) => (
              <Form>
                <div className="flex flex-col gap-6">
                  <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
                    <div className="grid gap-3">
                      <label
                        htmlFor="title"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Title <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="text"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 placeholder:text-[14px] focus:border-primary ${
                            errors.title && touched.title
                              ? "!border-red-500"
                              : ""
                          }`}
                          autoComplete="off"
                          name="title"
                          disabled={true}
                          placeholder="Enter title"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                        />
                        <FieldErrorMessage name="title" />
                      </div>
                    </div>

                    {/* Status Field */}
                    <div className="grid gap-3">
                      <label
                        htmlFor="status"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Status <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Select
                          showSearch
                          style={{ width: "100%" }}
                          placeholder="Select Status"
                          optionFilterProp="children"
                          className={`ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary ${errors.status ? "!border-red-500" : ""}`}
                          name="status"
                          suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                          onChange={(value) => {
                            setFieldValue("status", value);
                          }}
                          value={values.status}
                        >
                          {ComplianceStatusList.map((item, index) => (
                            <Select.Option value={item.value} key={index}>
                              {item.name}
                            </Select.Option>
                          ))}
                        </Select>
                        <FieldErrorMessage name="status" />
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <label
                        htmlFor="email"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Expiry date
                      </label>
                      <div className="expiry-date flex flex-col">
                        <DatePicker
                          format={"DD/MM/YYYY"}
                          // allowClear={false}
                          type="expirydate"
                          className={`w-full rounded-lg border border-lightGrey px-2 !outline-none !ring-0 placeholder:text-[14px] focus:border-primary ${errors.expirydate && touched.expirydate ? "!border-red-500" : ""}`}
                          autoComplete="off"
                          name="expirydate"
                          placeholder="Select Expiry date"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                          onChange={(date) => {
                            setFieldValue(
                              "expirydate",
                              date?.format("DD/MM/YYYY"),
                            ); // Update Formik value
                          }}
                          value={values.expirydate}
                        />
                        <FieldErrorMessage name="expirydate" />
                      </div>
                    </div>

                    {/*  */}
                    <div className="grid gap-3">
                      <label
                        htmlFor="title"
                        className="flex items-center justify-between gap-2 text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        <span>
                          Attachment <RequiredSymbol />
                        </span>{" "}
                        {selectedDocumentDetails[0]?.allow_multi_upload ? (
                          <span className="text-[12px] font-normal text-txtColor">
                            ( Multiple Attachment Allowed )
                          </span>
                        ) : null}
                      </label>
                      <div className="relative w-full">
                        <div className="flex items-start justify-between gap-3 rounded-lg border border-lightGrey px-2 py-1">
                          <div className="flex grow flex-wrap gap-3">
                            {values.compliance_file.filter(
                              (file) => file !== "null",
                            ).length == 0 ? (
                              <>
                                <span className="m-1 text-[14px] font-medium text-txtColor">
                                  No file chosen
                                </span>
                              </>
                            ) : null}
                            {values.compliance_file.map((file, index) => {
                              if (file && file !== "null") {
                                return (
                                  <span
                                    key={`existing-${index}`}
                                    className="flex items-center gap-2 truncate rounded-md bg-lightblue px-3 py-[6px] text-sm text-primary"
                                  >
                                    {file.name ? file.name : file}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Remove the file from the array and place null in its place
                                        let updatedFiles =
                                          values.compliance_file.map((f) =>
                                            f === file ? "null" : f,
                                          );
                                        setFieldValue(
                                          "compliance_file",
                                          updatedFiles,
                                        );
                                      }}
                                      className="text-red-500"
                                    >
                                      <SelectRemoveIcon />
                                    </button>
                                  </span>
                                );
                              }
                            })}
                          </div>
                          <label
                            htmlFor="file-upload"
                            className="ease flex h-[30px] w-[40px] items-center justify-between rounded-lg text-[14px] font-medium leading-[normal] text-black"
                          >
                            <HelpReportFeebackUploadIcon
                              cls={"text-txtColor w-[30px] h-[18px]"}
                            />
                          </label>
                          <input
                            id="file-upload"
                            name="compliance_file"
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                            className="absolute inset-0 h-full w-[10px] cursor-pointer opacity-0"
                            onChange={(e) => {
                              handleFileChange(e, setFieldValue, values);
                            }}
                            multiple // Allow multiple file selection
                          />
                        </div>

                        <FieldErrorMessage name="compliance_file" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 items-center justify-normal gap-4 sm:grid-cols-2">
                      <div className="grid gap-3">
                        <label
                          htmlFor="notApplicable" // Matches the id of the checkbox
                          className="cursor-pointer select-none text-[14px] font-medium leading-[normal] text-txtColor"
                        >
                          <Field
                            type="checkbox"
                            id="notApplicable" // Add an id to match the label's htmlFor
                            name="notApplicable"
                            className="mr-3 cursor-pointer"
                            checked={values.notApplicable} // Correctly bind the state
                            onChange={(e) => {
                              setFieldValue("notApplicable", e.target.checked); // Update the Formik state
                            }}
                          />
                          Not Applicable
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-[20px]">
                    <button
                      type="button"
                      className="transition-linear relative h-[45px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary"
                      onClick={submitForm}
                    >
                      {isSubmitting ? <ButtonLoader /> : <span>Edit</span>}
                    </button>

                    <button
                      type="button"
                      className="transition-linear h-[45px] w-[120px] rounded-lg border border-primary bg-none text-[14px] font-medium text-primary hover:bg-primary hover:text-white"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </ModalPopUp>
  );
};

export default EditBankCandidateComplianceModal;
